import SpecialServiceRequestDetails from 'components/RequestsOverview/components/RequestDetailsDrawer/components/OverviewTab/components/RequestItemCard/components/SpecialServiceRequestDetails/View';
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DrawerFrame from 'components/components/Drawer/Frame';
import DataColumns from 'components/components/DataColumns';
import StaticNotification from 'components/components/Notification/StaticNotification';
import DetailsTabs from 'components/RequestsOverview/components/RequestDetailsDrawer/Tabs';
import DiscussionBoard from 'components/RequestsOverview/components/RequestDetailsDrawer/components/DiscussionBoard/View';
import DocumentsTab from 'components/RequestsOverview/components/RequestDetailsDrawer/components/DocumentsTab/View';
import RequestItemCard from 'components/RequestsOverview/components/RequestDetailsDrawer/components/OverviewTab/components/RequestItemCard/View';
import AgreementField from 'components/RequestsOverview/components/RequestDetailsDrawer/components/OverviewTab/components/AgreementField';
import ReasonForDenying from 'components/RequestsOverview/components/RequestDetailsDrawer/components/OverviewTab/components/ReasonForDenying/View';
import ServiceRequestTotalContent from 'components/RequestServiceDrawer/components/ServiceRequestTotal/Content';
import { convertGetServiceRequestItemResponse, toRequestComment } from 'packages/service_request_repository/utils/converters';
import { PortalRequest, PortalScope } from 'packages/venture_builder_repository/portal/types';
import { useDecidePortalQuote, usePortalRequest, usePortalRequestItems } from 'packages/venture_builder_repository/portal/hooks';
import {
  addPortalComment, getPortalComments, getPortalDocuments, getPortalInvoices,
} from 'packages/venture_builder_repository/portal/requestDetails';
import { portalKeys } from 'packages/venture_builder_repository/portal/utils';
import { RequestDetailsTabs } from 'types/requests';
import {
  LoadError, Loading, money, RequestTitle, startupLabel,
} from '../../shared';

const SentRequestDrawer = ({ scope, request, onClose }: {
  scope: PortalScope, request: PortalRequest, onClose: () => void,
}) => {
  const [tab, setTab] = useState<RequestDetailsTabs>('Overview');
  const [displayApprove, setDisplayApprove] = useState(true);
  const details = usePortalRequest(scope, request.startup.id, request.id);
  const items = usePortalRequestItems(scope, request.startup.id, request.id);
  const decision = useDecidePortalQuote(scope);
  const client = useQueryClient();
  const key = [...portalKeys.requests(scope, request.startup.id), 'details', request.id];
  const comments = useQuery({
    queryKey: [...key, 'comments'],
    queryFn: () => getPortalComments(request.startup.id, request.id),
    enabled: tab === 'Discussion board',
    retry: false,
  });
  const documents = useQuery({
    queryKey: [...key, 'documents'],
    queryFn: () => getPortalDocuments(request.startup.id, request.id),
    enabled: tab === 'Documents',
    retry: false,
  });
  const invoices = useQuery({
    queryKey: [...key, 'invoices'],
    queryFn: () => getPortalInvoices(request.startup.id, request.id),
    enabled: tab === 'Documents',
    retry: false,
  });
  const comment = useMutation({
    mutationFn: (text: string) => addPortalComment(scope, request.startup.id, request.id, text),
    onSuccess: () => client.invalidateQueries({ queryKey: [...key, 'comments'] }),
  });
  const current = details.data;
  const pending = decision.isPending || comment.isPending;
  const decide = async (approve: boolean, reason?: string) => {
    if (!current || !items.data) return false;
    try {
      await decision.mutateAsync({
        startupId: request.startup.id,
        requestId: request.id,
        reviewedItems: items.data,
        reviewedTotal: current.totalPrice,
        approve,
        reason,
      });
      setDisplayApprove(true);
      return true;
    } catch {
      return false;
    }
  };
  return (
    <DrawerFrame
      title={<RequestTitle items={items.data || []} status={current?.status ?? request.status} />}
      className="drawer--blue"
      open
      onClose={() => { if (!pending) onClose(); }}
      closable={!pending}
      maskClosable={!pending}
      keyboard={!pending}
    >
      {(details.isPending || items.isPending) && <Loading />}
      {(details.error || items.error) && (
        <LoadError error={(details.error || items.error)!} retry={() => { details.refetch(); items.refetch(); }} />
      )}
      {current && items.data && !details.error && !items.error && (
        <DetailsTabs
          activeTab={tab}
          onChange={setTab}
          overview={(
            <div className="overview-tab-content vb-portal__request-details">
              <DataColumns columns={[
                { label: 'Startup', value: startupLabel(request.startup) },
                { label: 'Service provider', value: request.serviceProvider?.name || 'Not specified' },
              ]}
              />
              {items.data.map((item) => {
                const converted = convertGetServiceRequestItemResponse({
                  ...item,
                  serviceRequest: undefined,
                  serviceID: item.serviceID || item.service?.id || 0,
                  servicePriceID: item.servicePriceID || 0,
                  rate: item.rate || 0,
                  fee: item.fee || 0,
                  icp: item.icp || 0,
                  details: item.details || '{}',
                  service: item.service ? {
                    ...item.service, category: item.service.category || { id: item.service.categoryID || 0 },
                  } : undefined,
                });
                return (
                  <RequestItemCard
                    key={item.id}
                    item={converted}
                    actions={{ updateRequestItemById: () => {} }}
                    specialDetails={(
                      <SpecialServiceRequestDetails
                        serviceRequestId={request.id}
                        service={converted.service}
                        details={converted.details}
                        requestDocuments={documents.data}
                        actions={{ getServiceRequestDocuments: () => { documents.refetch(); } }}
                      />
                    )}
                  />
                );
              })}
              <ServiceRequestTotalContent>{money(current.totalPrice)}</ServiceRequestTotalContent>
              {current.status === 3 && (
                <ReasonForDenying
                  userType="Venture Builder"
                  onDeny={(reason) => decide(false, reason)}
                  onApprove={() => { decide(true); }}
                  pending={pending}
                  approvalDisabled={details.isFetching || items.isFetching}
                  displayApproveBtn={displayApprove}
                  setDisplayApproveBtn={setDisplayApprove}
                  showInsufficientFunds={false}
                />
              )}
              {decision.error && <StaticNotification type="warning" message={decision.error.message} />}
            </div>
          )}
          discussion={(
            <>
              {comments.error ? <LoadError error={comments.error} retry={() => { comments.refetch(); }} /> : (
                <DiscussionBoard
                  userType="Venture Builder"
                  isLoading={comments.isPending || comment.isPending}
                  disableComment={[6, 7, 8].includes(current.status)}
                  comments={(comments.data || []).map(toRequestComment)}
                  onComment={async (text) => {
                    try { await comment.mutateAsync(text); return true; } catch { return false; }
                  }}
                />
              )}
              {comment.error && <StaticNotification type="warning" message={comment.error.message} />}
            </>
          )}
          documents={(
            <>
              <AgreementField fileName="Work Order.pdf" fileUrl={current.workOrder} altText="No Work Order associated with this request" />
              {documents.isPending && <Loading />}
              {documents.error ? <LoadError error={documents.error} retry={() => { documents.refetch(); }} /> : (
                <DocumentsTab userType="Venture Builder" serviceRequestId={request.id} requestDocuments={documents.data} actions={{ getServiceRequestDocuments: () => {} }} />
              )}
              {invoices.error && <LoadError error={invoices.error} retry={() => { invoices.refetch(); }} />}
              {invoices.data?.map((invoice) => <AgreementField key={invoice.id} fileName={`Invoice ${invoice.invoiceNumber}.pdf`} fileUrl={invoice.invoiceFile} />)}
            </>
          )}
        />
      )}
    </DrawerFrame>
  );
};

export default SentRequestDrawer;
